import { MONTH_NAMES } from "@/constants/date"

import { getUnixTime } from "date-fns"
import { isNil, isNaN, isString, isNumber, isArray } from "lodash"

import { isDate, isEmpty } from "@/utils/typeOf"
import { SquareUnit } from "@/constants/unit"

/** 넘어온 paramList를 Param형식으로 변환
  {key:undefine} => {}
  {key:DateTime} => {key:Timestamp]} 
  {key:value} => {key:"value"}
*/
export function makeParams<T>(paramList: T & Record<string, unknown>) {
  return Object.keys(paramList).reduce(
    (result, key) => {
      if (!isEmpty(paramList[key])) {
        if (isDate(paramList[key])) {
          result[key] = getUnixTime(paramList[key] as Date)
        } else {
          result[key] = paramList[key]
        }
      }

      return result
    },
    {} as Record<string, unknown>,
  )
}

/**
 * @param {object} params [Required]
 * @desc 넘어온 paramList를 URL 형식으로 변환, {key:["value1","","value2"]} => "/key/value1,value2"
 */
export function makeUrl<T>(params: Record<string, T>) {
  return Object.entries(params).reduce((acc, cur) => {
    const key = cur[0]
    const value = cur[1]

    if (isString(value) || isNumber(value)) {
      return `${acc}/${key}/${value}`
    }

    if (isDate(value)) {
      return `${acc}/${key}/${getUnixTime(value as Date)}`
    }

    if (isArray(value) && (value as T[]).length > 0) {
      return `${acc}/${key}/${(value as T[]).filter((item) => !isEmpty(item)).join(",")}`
    }

    return acc
  }, "")
}

export function makeUrlParams<T>(paramList: Record<string, T>) {
  let urlParams = ""

  for (const key in paramList) {
    let urlParam = ""

    if (isNil(paramList[key]) || isNaN(paramList[key] as number)) {
      continue
    }

    if (isArray(paramList[key])) {
      if ((paramList[key] as Array<T>).length > 0) {
        urlParam = `/${key}/`
        urlParam += (paramList[key] as Array<T>)
          .filter((param) => param !== "" && !isNil(param) && !isNaN(param))
          .join(",")
      }
    } else if (isString(paramList[key])) {
      if ((paramList[key] as string).length > 0) {
        urlParam = `/${key}/`
        urlParam += paramList[key]
      }
    } else if (isDate(paramList[key])) {
      urlParam = `/${key}/`
      urlParam += getUnixTime(paramList[key] as Date)
    } else {
      urlParam = `/${key}/`
      urlParam += paramList[key]
    }

    urlParams += urlParam
  }

  return urlParams
}

export const fixedNumberDropZero = (value?: number, fixedNumber?: number) => {
  if (value === undefined || isNaN(value)) {
    return undefined
  }

  return parseFloat(value.toFixed(fixedNumber ?? 1))
}
/**
 *
 * @param localeString 1,234.56과 같은 문자열
 * @returns 파싱된 숫자
 *
 * by GPT 4o
 */
export function localeStringToNumber(localeString: string): number {
  const locale = navigator.language
  // 해당 로케일에서 사용하는 숫자 형식의 예를 생성합니다.
  const example = (12345.67).toLocaleString(locale)

  // 예제를 기준으로 천 단위 구분자와 소수점 구분자를 추출합니다.
  const separators = example.match(/[^0-9]/g)
  const thousandSeparator = separators?.[0] || ","
  const decimalSeparator = separators?.[1] || "."

  // 문자열 아닌 경우 type error 방지
  if (localeString === "" || !localeString || typeof localeString !== "string") {
    return 0
  }
  // 천 단위 구분자가 동일 문자열에서 여러 번 나타날 수 있으므로 모두 제거합니다.
  // 소수점 구분자를 표준 소수점으로 변경합니다.
  const normalizedString = localeString
    .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
    .replace(new RegExp(`\\${decimalSeparator}`), ".")

  // 숫자로 변환합니다.
  return parseFloat(normalizedString)
}

/**
 *
 * @param duration '1’ 30”과 같은 문자열
 * @returns 초 단위로 변환된 숫자
 */
export function durationToNumber(duration: string): number {
  const [minutes, seconds] = duration.split("’")

  return Number(minutes) * 60 + Number(seconds.trim().split("”")[0])
}

export function convertUnitSquare(unit: string): string {
  const unitList = Object.keys(SquareUnit)
  const unitIndex = unitList.findIndex((item) => item === unit)

  if (unitIndex !== -1) {
    return SquareUnit[unitList[unitIndex]]
  }

  return unit
}

/** matchDay 라벨("MD-3" 등) → 정렬용 수치. MD-n 은 n, MD/MD(H)/MD(A)/MD+n 은 0, 그 외 999 */
export const parseMatchDay = (matchDay: string): number => {
  if (!matchDay) {
    return 999
  }
  const match = matchDay.match(/MD-?(\d+)?/)
  if (match) {
    return match[1] ? parseInt(match[1], 10) : 0
  }

  return 999
}

/** 시즌 위크 라벨("2025 W3" / "Week 3") → 정렬용 {year, week} */
export const parseSeasonWeek = (week: string): { year: number; week: number } => {
  if (!week) {
    return { year: 9999, week: 999 }
  }

  const match = week.match(/(\d{4})\s*W(\d+)/)
  if (match) {
    return { year: parseInt(match[1], 10), week: parseInt(match[2], 10) }
  }

  const weekMatch = week.match(/Week\s*(\d+)/i)
  if (weekMatch) {
    return { year: 0, week: parseInt(weekMatch[1], 10) }
  }

  return { year: 9999, week: 999 }
}

/** 월 그룹 라벨("2025 May") → 정렬용 {year, month(1-12)} */
export const parseMonth = (monthStr: string): { year: number; month: number } => {
  if (!monthStr) {
    return { year: 9999, month: 99 }
  }
  const parts = monthStr.split(" ")
  if (parts.length === 2) {
    const year = parseInt(parts[0], 10)
    const monthIndex = MONTH_NAMES.indexOf(parts[1])
    if (!isNaN(year) && monthIndex !== -1) {
      return { year, month: monthIndex + 1 }
    }
  }

  return { year: 9999, month: 99 }
}
